import omit from 'lodash/omit';

import type {MetricEntry, QueryFormulaEntry, QueryMetricEntry} from 'common/query-builder/query-entry-section/types';
import {getShowSection} from 'common/report/insights/util/events';
import {BehaviorShowClause, FormulaShowClause, ShowClauseFormula} from 'common/report/insights/util/types';
import {getAlphaLabelFromIndex} from 'common/util';
import {Metric} from 'app_api/projects/metrics/types';

import {get_parser as getFormulaParser, Token, UnexpectedCharacters, UnexpectedToken} from './formula-parser-compiled';

const DefaultFormulaNamePrefix = `Formula`;

export function getNextFormulaName({existingFormulaNames}: {existingFormulaNames: string[]}): string {
  const getFormulaNumber = () => {
    const defaultFormulaNameRegex = RegExp(`^${DefaultFormulaNamePrefix} (\\d+)$`);
    const existingFormulaNumbers = existingFormulaNames
      .map((name) => name.match(defaultFormulaNameRegex)?.[1])
      .filter((numStr) => numStr)
      .map((numStr) => Number(numStr));
    return Math.max(existingFormulaNames.length, ...existingFormulaNumbers) + 1;
  };

  return existingFormulaNames.length === 0
    ? DefaultFormulaNamePrefix
    : `${DefaultFormulaNamePrefix} ${getFormulaNumber()}`;
}

export function getFormulaEntryTrackingProps(entry: QueryFormulaEntry) {
  if (entry.event.id) {
    return {};
  }

  const operatorsCount = (entry.event.definition?.match(/\*|\/|\+|-/g) || []).length;
  const operandsCount = (entry.event.definition?.match(/[A-Z]+/g) || []).length;
  return {
    name: entry.event.name,
    definition: entry.event.definition,
    operandsCount,
    operatorsCount,
  };
}

/**
 * For a formula and all the metrics it references, we need to map
 * existing positions in the report entries to what they would become
 * inside a saved formula referenced metrics array.
 */
export function getUpdatedFormulaAndMetrics(
  formula: ShowClauseFormula,
  entries: Array<QueryMetricEntry | MetricEntry>,
): {definition: string; referencedMetrics: BehaviorShowClause[]} {
  const formulaOperands = new Set(formula.definition.match(/[A-Z]{1,}/g));

  // a map of the metrics referenced by the inline formula
  // to the variable identifier they are currently using
  const referencedEntries: Array<QueryMetricEntry | MetricEntry> = [];
  const updatedFormulaMap: Record<string, string> = {};

  for (const [index, entry] of entries.entries()) {
    const identifier = getAlphaLabelFromIndex(index);
    if (formulaOperands.has(identifier)) {
      const newIdentifier = getAlphaLabelFromIndex(referencedEntries.length);
      referencedEntries.push(entry);
      updatedFormulaMap[identifier] = newIdentifier;
    }
  }

  // saving a constant formula
  if (referencedEntries.length === 0) {
    return {
      definition: formula.definition,
      referencedMetrics: [],
    };
  }

  return {
    definition: replaceFormulaReferences(formula.definition, updatedFormulaMap),
    referencedMetrics: getShowSection({entries: referencedEntries})
      .toBookmarkData()
      .map((metric) => omit(metric, `isHidden`, `isExpanded`)),
  };
}

export class FormulaParseError extends Error {}

let _parser;

/**
 * Parses a formula and returns the AST.
 */
export function parseFormula(formula: string, availableVariables: string[]) {
  try {
    _parser ||= getFormulaParser();

    const tree = _parser.parse(formula);

    // ensure only variables that are available are used
    for (const token of tree.scan_values((node) => node instanceof Token && node.type === `VAR`)) {
      if (!availableVariables.includes(token.value)) {
        throw new FormulaParseError(`Unknown variable: ${token.value}`);
      }
    }

    return tree;
  } catch (err) {
    if (err instanceof UnexpectedToken) {
      if (err.token.type === `$END`) {
        throw new FormulaParseError(`Incomplete formula`);
      }

      throw new FormulaParseError(`Unexpected token: ${err.token.value}`);
    } else if (err instanceof UnexpectedCharacters) {
      throw new FormulaParseError(`Unexpected character: ${err.char}`);
    }

    throw err;
  }
}

export function getMetricUpdateFromFormulaShowClause(clause: FormulaShowClause): Metric {
  return {
    type: `formula`,
    id: clause.formula.id,
    name: clause.formula.name,
    definition: {
      formula: {
        definition: clause.formula.definition,
        referencedMetrics: clause.formula.referencedMetrics,
      },
      measurement: clause.measurement,
    },
  };
}

/**
 * Updates a formula definition by replacing old labels with new labels.
 *
 * @param referenceMap - Mapping of old labels to new labels
 */
export function replaceFormulaReferences(formula: string, referenceMap: Record<string, string>) {
  return formula.replace(/\w+/g, (sub) => {
    // if the label is in the reference map, replace it with the new label. otherwise we'll leave it alone
    return referenceMap[sub] || sub;
  });
}
